import { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLogout } from "@hooks/useLogout";
import { openModal } from "@store/slices/modalSlice";
import { MODAL_KEYS } from "@constants/global.constants";
import type { AppDispatch } from "@store/store";

/**
 * Custom hook for handling click outside events
 */
const useClickOutside = (ref: React.RefObject<HTMLDivElement | null>, handler: () => void, active: boolean) => {
	useEffect(() => {
		if (!active) return;

		const handleClick = (event: MouseEvent) => {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				handler();
			}
		};

		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [ref, handler, active]);
};

/**
 * Custom hook for profile dropdown functionality
 * @returns Object containing dropdown state and handlers
 */
export const useProfileDropdown = () => {
	const dispatch = useDispatch<AppDispatch>();
	const [showMenu, setShowMenu] = useState(false);
	const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);

	// Get logout functionality from useLogout hook
	const { handleLogout, handleLogoutConfirm, handleLogoutCancel, showLogoutModal } = useLogout();

	const handleToggle = useCallback(() => setShowMenu(prev => !prev), []);
	const handleClose = useCallback(() => setShowMenu(false), []);

	useClickOutside(menuRef, handleClose, showMenu);

	const ShowProfile = useCallback(() => {
		setShowMenu(false);
		dispatch(openModal(MODAL_KEYS.PROFILE));
	}, [dispatch]);

	const ShowChangePassword = useCallback(() => {
		setShowMenu(false);
		setShowChangePasswordModal(true);
	}, []);

	const HideChangePassword = useCallback(() => {
		setShowChangePasswordModal(false);
	}, []);

	return {
		showMenu,
		menuRef,
		handleToggle,
		handleLogout,
		handleLogoutConfirm,
		handleLogoutCancel,
		showLogoutModal,
		ShowProfile,
		ShowChangePassword,
		HideChangePassword,
		showChangePasswordModal,
	};
};
